import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { GoogleMapsModule, MapAdvancedMarker, MapGeocoder, MapInfoWindow } from '@angular/google-maps';
import { TranslocoDirective } from "@jsverse/transloco";
import { ButtonModule } from "primeng/button";
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map as rxjsMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-map-dialog',
    standalone: true,
    imports: [
        CommonModule, 
        GoogleMapsModule, 
        ButtonModule, 
        TranslocoDirective
    ],
    templateUrl: './map-dialog.component.html',
    styleUrls: ['./map-dialog.component.scss']
})
export class MapDialogComponent implements OnInit {
    @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;

    points: any[] = [];
    selectedPoint: any = null;
    apiLoaded: Observable<boolean>;

    constructor(
        public config: DynamicDialogConfig,
        public ref: DynamicDialogRef,
        private geocoder: MapGeocoder,
    ) {}

    ngOnInit() {
        this.mapDynamicLoading();

        if (this.config.data?.points) {
            this.points = this.config.data.points;
        }
    }

    // TODO: Replace the dynamic loading of the Google Maps Library 
    // from the Inline Bootstrap Loader with @googlemaps/js-api-loader
    private mapDynamicLoading() {
        this.apiLoaded = new Observable<boolean>((observer) => {
            // Check if the API has already been loaded
            if (typeof google !== 'undefined' && google.maps) {
                observer.next(true);
                observer.complete();
                return;
            }

            // Creating an Inline Bootstrap Loader (recommended by Google)
            const bootstrapScript = document.createElement('script');
            bootstrapScript.innerHTML = `
                (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Recomends: https://goo.gle/js-api-loading"):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
                    key: ${JSON.stringify(environment.googleMapApiKey)},
                    v: "beta"
                });
            `;
            document.head.appendChild(bootstrapScript);

            // Downloading the necessary parts
            Promise.all([
                (google.maps as any).importLibrary("maps"),
                (google.maps as any).importLibrary("marker")
            ]).then(() => {
                observer.next(true);
                observer.complete();
            }).catch((err) => {
                console.error("Library loading error:", err);
                observer.error(err);
            });
        }).pipe(
            catchError(() => of(false))
        );
    }

    onMapInitialized(map: google.maps.Map) {
        if (!map || this.points.length === 0) return;

        const bounds = new google.maps.LatLngBounds();

        const tasks$ = this.points.map(point => {
            if (point.isUserLocation) {
                const locationAddress = `${point.street} ${point.houseNumber}, ${point.postalCode} ${point.city} ${point.country}`;
                return this.geocoder.geocode({ address: locationAddress }).pipe(
                    rxjsMap(({ results, status }) => {
                        if (status === 'OK' && results.length > 0) {
                            const loc = results[0].geometry.location;
                            point.lat = loc.lat();
                            point.lng = loc.lng();
                        }
                        return point;
                    }),
                    catchError(() => of(point))
                );
            } else {
                return of(point);
            }
        });

        forkJoin(tasks$).subscribe((updatedPoints) => {
            updatedPoints.forEach(point => {
                if (point.lat && point.lng) {
                    bounds.extend({ lat: point.lat, lng: point.lng });
                }
            });

            map.fitBounds(bounds);
        });

        const listener = google.maps.event.addListener(map, 'idle', () => {
            if (map.getZoom()! > 17) map.setZoom(17);
            google.maps.event.removeListener(listener);
        });
    }

    openInfoWindow(markerElem: MapAdvancedMarker, point: any) {
        this.selectedPoint = point;
        this.infoWindow.open(markerElem, false);
    }

    selectPoint(point: any) {
        this.ref.close(point);
    }

    onMapClick() {
        if (this.infoWindow) {
            this.infoWindow.close();
        }
    }
}